import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

const GlobalLeaderboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [years, setYears] = useState([]);
  const [courses, setCourses] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [filters, setFilters] = useState({
    year: 'all',
    course: 'all',
    college: 'all',
    sort: 'engineer_score',
    page: 1
  });
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [filters]);

  const fetchFilters = async () => {
    try {
      const yearRes = await api.get('/leaderboard/years');
      setYears(yearRes.data.years);
      
      const courseRes = await api.get('/leaderboard/courses');
      setCourses(courseRes.data.courses);

      const collegeRes = await api.get('/auth/colleges');
      setColleges(collegeRes.data.colleges || []);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await api.get('/leaderboard', {
        params: {
          type: 'global',
          year: filters.year,
          course: filters.course,
          college_id: filters.college,
          sort: filters.sort,
          page: filters.page,
          limit: 50
        }
      });
      setLeaderboard(response.data.leaderboard);
      setMeta(response.data.meta);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeStyle = (score) => {
    if (score >= 900) return 'bg-yellow-100 text-yellow-700 border border-yellow-300';
    if (score >= 800) return 'bg-purple-100 text-purple-700 border border-purple-300';
    if (score >= 700) return 'bg-indigo-100 text-indigo-700 border border-indigo-300';
    if (score >= 550) return 'bg-blue-100 text-blue-700 border border-blue-300';
    if (score >= 400) return 'bg-green-100 text-green-700 border border-green-300';
    if (score >= 200) return 'bg-orange-100 text-orange-700 border border-orange-300';
    return 'bg-gray-100 text-gray-700 border border-gray-300';
  };

  const getBadgeIcon = (score) => {
    if (score >= 900) return 'bx-crown';
    if (score >= 800) return 'bx-medal';
    if (score >= 700) return 'bx-trophy';
    if (score >= 550) return 'bx-star';
    if (score >= 400) return 'bx-trending-up';
    if (score >= 200) return 'bx-book-open';
    return 'bx-rocket';
  };

  const getBadgeLabel = (score) => {
    if (score >= 900) return 'Elite';
    if (score >= 800) return 'Expert';
    if (score >= 700) return 'Advanced';
    if (score >= 550) return 'Strong';
    if (score >= 400) return 'Intermediate';
    if (score >= 200) return 'Learner';
    return 'Beginner';
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const sortOptions = [
    { value: 'engineer_score', label: 'Global Engineer Score' },
    { value: 'cf', label: 'Codeforces Rating' },
    { value: 'lc', label: 'LeetCode Rating' },
    { value: 'cc', label: 'CodeChef Rating' }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
          <i className='bx bx-world text-blue-600'></i>
          <span>Global Leaderboard</span>
        </h1>
        <p className="text-gray-600 mt-1">Compare yourself with students across the platform</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <i className='bx bx-filter text-lg text-gray-700'></i>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filters</h3>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-40">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Graduation Year
            </label>
            <select
              value={filters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="w-40">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Course
            </label>
            <select
              value={filters.course}
              onChange={(e) => handleFilterChange('course', e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Courses</option>
              {courses.map((course) => (
                <option key={course} value={course}>
                  {course}
                </option>
              ))}
            </select>
          </div>

          <div className="w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              College
            </label>
            <select
              value={filters.college}
              onChange={(e) => handleFilterChange('college', e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Colleges</option>
              {colleges.map((college) => (
                <option key={college.college_id} value={college.college_id}>
                  {college.name_display}
                </option>
              ))}
            </select>
          </div>

          <div className="w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          {/* User Rank Card */}
          {user && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg shadow-sm overflow-hidden p-3 md:p-4">
               <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                 <div className="flex items-center space-x-3 md:space-x-6">
                     <div className="text-center min-w-[50px]">
                         <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mb-0.5">Your Rank</div>
                         <div className="text-xl md:text-2xl font-bold text-blue-800">{meta?.userRank ? `#${meta.userRank}` : 'N/A'}</div>
                     </div>
                     <div className="h-8 md:h-10 w-px bg-blue-200"></div>
                     <div className="flex items-center">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center mr-3 border-2 border-blue-200 overflow-hidden shadow-sm">
                          {user.profile_photo ? (
                            <img 
                              src={user.profile_photo.startsWith('http') ? user.profile_photo : `${process.env.REACT_APP_IMG_URL || 'http://localhost:5000'}${user.profile_photo}`} 
                              alt="Profile" 
                              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover" 
                            />
                          ) : (
                            <i className='bx bx-user text-xl text-gray-400'></i>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                             <div className="text-sm md:text-base font-bold text-gray-900">{user.name}</div>
                             <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold shadow-sm flex items-center ${getBadgeStyle(user.global_engineer_score || 0)}`}>
                               <i className={`bx ${getBadgeIcon(user.global_engineer_score || 0)} mr-0.5`}></i>
                               {getBadgeLabel(user.global_engineer_score || 0)}
                             </span>
                          </div>
                          <div className="text-xs text-gray-600 flex items-center mt-0.5">
                             <i className='bx bxs-school mr-1'></i>
                             <span className="truncate max-w-[150px] md:max-w-xs">{user.college?.name}</span>
                          </div>
                        </div>
                     </div>
                 </div>
                 
                 <div className="flex items-center justify-between md:justify-end space-x-2 md:space-x-6 w-full md:w-auto bg-blue-100/50 md:bg-transparent rounded-lg p-2 md:p-0">
                     <div className="text-center px-2">
                        <div className="text-[10px] text-gray-500 font-semibold mb-0.5">Global Score</div>
                        <div className="font-bold text-blue-600 text-lg md:text-xl">{user.global_engineer_score || 0}</div>
                     </div>
                     <div className="h-6 w-px bg-gray-300 md:hidden"></div>
                     <div className="text-center px-1">
                        <span className="text-[10px] text-gray-500 block font-medium mb-0.5">Codeforces</span>
                        <span className="font-bold text-gray-800 text-sm md:text-base">{user.ratings?.codeforces || '-'}</span>
                     </div>
                     <div className="text-center px-1">
                        <span className="text-[10px] text-gray-500 block font-medium mb-0.5">LeetCode</span>
                        <span className="font-bold text-gray-800 text-sm md:text-base">{user.ratings?.leetcode || '-'}</span>
                     </div>
                     <div className="text-center px-1">
                        <span className="text-[10px] text-gray-500 block font-medium mb-0.5">CodeChef</span>
                        <span className="font-bold text-gray-800 text-sm md:text-base">{user.ratings?.codechef || '-'}</span>
                     </div>
                 </div>
               </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      College
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Global Score
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Codeforces
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      LeetCode
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      CodeChef
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leaderboard.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        {student.rank <= 3 ? (
                          <span className="text-xl mr-1">
                            {student.rank === 1 ? '🥇' : student.rank === 2 ? '🥈' : '🥉'}
                          </span>
                        ) : null}
                        <span className="text-sm font-bold text-gray-900">#{student.rank}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center mr-3 overflow-hidden">
                          {student.profile_photo ? (
                            <img 
                              src={student.profile_photo.startsWith('http') ? student.profile_photo : `${process.env.REACT_APP_IMG_URL || 'http://localhost:5000'}${student.profile_photo}`} 
                              alt="Profile" 
                              className="w-10 h-10 rounded-full object-cover" 
                            />
                          ) : (
                            <i className='bx bx-user text-xl text-gray-500'></i>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900 w-40 truncate" title={student.name}>{student.name}</div>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-sm flex items-center ${getBadgeStyle(student.global_engineer_score || 0)}`}>
                              <i className={`bx ${getBadgeIcon(student.global_engineer_score || 0)} mr-0.5`}></i>
                              {getBadgeLabel(student.global_engineer_score || 0)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">{student.course}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 max-w-[200px]">
                      <div className="text-sm text-gray-900 truncate" title={student.college}>{student.college}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {student.graduation_year}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="text-sm font-bold text-blue-600">
                        {student.global_engineer_score}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {student.ratings?.codeforces || '-'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {student.ratings?.leetcode || '-'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {student.ratings?.codechef || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>

          {/* Pagination */}
          {meta && meta.total_pages > 1 && (
            <div className="mt-6 flex justify-center space-x-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {meta.page} of {meta.total_pages}
              </span>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page === meta.total_pages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GlobalLeaderboard;
